import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { Modal } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import _ from 'underscore';
import { cloneDeep } from 'lodash';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import ShipmentStore from 'stores/ShipmentStore';
import validator from 'email-validator';
import i18next from 'i18next';
import ProTip from '../../ui/ProTip';
import CarrierGroup from './CarrierGroup';

import {
  getDeliveryOrderRecipients,
  getShipmentEquipment,
} from '../../../helpers/shipments';

class BulkUpdateSendDOModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      saving: false,
      proTipVisible: true,
      carrierGroups: [],
      shipmentUpdates: {},
      error: false,
    };
  }

  componentDidMount() {
    this.initModalData(this.props);
    ShipmentStore.addChangeListener(this.handleShipmentsQueryUpdate);
    ShipmentStore.addBulkShipmentUpdateChangeListener(
      this.handleBulkShipmentUpdateChange,
    );
  }

  componentWillUnmount() {
    ShipmentStore.removeChangeListener(this.handleShipmentsQueryUpdate);
    ShipmentStore.removeBulkShipmentUpdateChangeListener(
      this.handleBulkShipmentUpdateChange,
    );
  }

  getShipmentMetadata = (shipments) => {
    return shipments.reduce((current, shipment) => {
      if (shipment.order && shipment.order._id) {
        const { _id, carrier } = shipment;

        const email = getDeliveryOrderRecipients(carrier) || '';
        return {
          ...current,
          [_id]: {
            _id,
            email,
            meta: {
              email,
            },
          },
        };
      }
      return current;
    }, {});
  };

  handleShipmentsQueryUpdate = async () => {
    const selectedShipments = ShipmentStore.getShipmentsForBulkUpdate() || [];
    const shipments = selectedShipments.filter((s) => s.carrier);
    if (shipments.length > 0) {
      const shipmentUpdates = this.getShipmentMetadata(shipments);
      const carrierGroups = groupShipmentsByCarrier(shipments);
      this.setState({ carrierGroups, shipmentUpdates });
    }
  };

  initModalData(props) {
    const { shipments } = props;
    ShipmentsActionCreators.getShipmentsById({
      shipments,
      current: [true, false],
      canceled: false,
    });
  }

  /**
   * @name hanldeSendAll  uses  the sendDeliveryOrder method to send emails
   * @params {object} options includes an array of shipments, the deliveryOrder
   * method, and an email address
   * @returns void
   */
  handleSendAll = async (jobId, emails) => {
    this.setState({ saving: true });
    await ShipmentsActionCreators.sendDeliveryOrder(jobId, emails);
    this.setState({ shipmentUpdates: {}, saving: false });
  };

  sendAll = () => {
    const { shipmentUpdates } = this.state;

    const shipmentsToUpdate = _.values(shipmentUpdates);

    const hasValidEmails = (shipment) => {
      if (shipment && shipment.meta) {
        const {
          meta: { email },
        } = shipment;
        const emails = email.replace(/' '/g, '').split(',') || '';

        if (!emails || !emails.length || !Array.isArray(emails)) {
          return false;
        }

        const invalidEmailAddresses = emails.filter(
          (ea) => !validator.validate(ea),
        );
        if (invalidEmailAddresses.length) {
          return false;
        }
        return true;
      }
      return false;
    };

    if (!shipmentsToUpdate.every(hasValidEmails)) {
      const error = 'INVALID_EMAILS';
      return this.setState({ error });
    }

    shipmentsToUpdate.forEach((shipment) => {
      const {
        meta: { email },
      } = shipment;
      const emails = email.replace(/' '/g, '').split(',') || '';
      this.handleSendAll(shipment._id, _.uniq(emails));
    });

    this.handleHide();
  };

  /**
   * @name hanldeRecipientsChange persists in react lifecycle user changes
   * @params {object} options includes shipmentOd and recipients
   * @returns void
   */
  handleRecipientsChange = (options) => {
    const { shipmentId, to } = options;
    const email = to;

    this.setState((prevState) => {
      const { shipmentUpdates: currentShipmentUpdates } = prevState;

      const target = cloneDeep(currentShipmentUpdates[shipmentId]);
      target.meta.email = email;
      const shipmentUpdates = {
        ...currentShipmentUpdates,
        [shipmentId]: target,
      };
      return {
        shipmentUpdates,
        error: false,
      };
    });
  };

  handleRemoveShipment = (carrierId, shipmentId) => {
    this.setState((prevState) => {
      const {
        carrierGroups: currentCarrierGroups,
        shipmentUpdates: currentShipmentUpdates,
      } = prevState;

      const { [shipmentId]: deletedKey, ...shipmentUpdates } =
        currentShipmentUpdates;
      const groupIndex = currentCarrierGroups.findIndex(
        (g) => g.id === carrierId,
      );

      const carrierGroup = cloneDeep(currentCarrierGroups[groupIndex]);

      const shipmentIndex = carrierGroup.shipments.findIndex(
        (s) => s._id === shipmentId,
      );

      const currentShipments = carrierGroup.shipments;
      carrierGroup.shipments = [
        ...currentShipments.slice(0, shipmentIndex),
        ...currentShipments.slice(shipmentIndex + 1),
      ];

      const carrierGroups = [
        ...currentCarrierGroups.slice(0, groupIndex),
        carrierGroup,
        ...currentCarrierGroups.slice(groupIndex + 1),
      ];

      return {
        carrierGroups,
        shipmentUpdates,
      };
    });
  };

  handleHide = () => this.props.onHide();

  handleBulkShipmentUpdateChange = () => {
    const { shipments } = this.props;
    ShipmentsActionCreators.getShipmentsById({ shipments });
  };

  handleApplyAll = (targetShipmentId) => {
    this.setState((prevState) => {
      const { shipmentUpdates: currentShipmentUpdates } = prevState;

      const shipmentGroup = this.state.carrierGroups
        .map((group) => {
          const found = group.shipments.some((s) => {
            return s._id === targetShipmentId;
          });
          if (group && found) {
            return group.shipments;
          }
          return false;
        })
        .filter((g) => {
          return !!g;
        });
      const shipmentsToUpdate = _.pluck(_.flatten(shipmentGroup), '_id');

      const update = currentShipmentUpdates[targetShipmentId];

      const {
        meta: { email },
      } = update;

      const shipmentUpdates = Object.keys(currentShipmentUpdates).reduce(
        (updates, shipmentId) => {
          if (shipmentsToUpdate.indexOf(shipmentId) > -1) {
            return {
              ...updates,
              [shipmentId]: {
                ...currentShipmentUpdates[shipmentId],
                meta: {
                  email,
                },
              },
            };
          }
          return {
            ...updates,
            [shipmentId]: {
              ...currentShipmentUpdates[shipmentId],
            },
          };
        },
        {},
      );
      return {
        shipmentUpdates,
      };
    });
  };

  renderProTip() {
    const { t } = this.props;
    if (!this.state.proTipVisible) {
      return null;
    }
    if (!this.state.carrierGroups.length) {
      return (
        <div className="mb-4">
          <ProTip
            message={t('ASSIGN_CARRIER_BEFORE_SEND_DELIVERY_TIP')}
            onClose={this.handleCloseTip}
          />
        </div>
      );
    }

    return (
      <div className="mb-4">
        <ProTip
          message={t('ENSURE_INFO_FILLED_BEFORE_APPLY_ALL')}
          onClose={this.handleCloseTip}
        />
      </div>
    );
  }

  render() {
    const { show, t } = this.props;

    const { carrierGroups, shipmentUpdates, saving, error } = this.state;

    return (
      <Modal
        show={show}
        onHide={this.handleHide}
        backdrop="static"
        dialogClassName="modal-override"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>{this.props.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            className="-m-4 p-4 bg-gray-100 overflow-y-auto"
            style={{ maxHeight: '80vh' }}
          >
            {this.renderProTip()}
            {carrierGroups.map((group, index) => (
              <CarrierGroup
                send_do={this.props.send_do}
                carrierGroup={group}
                saving={saving}
                shipmentUpdates={shipmentUpdates}
                onEmailChange={this.handleRecipientsChange}
                onApplyToAll={this.handleApplyAll}
                onRemoveShipment={this.handleRemoveShipment}
                key={group.id}
                disableSave={false}
              />
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end">
            <div className="md:w-1/4 mt-3">
              <div className="flex justify-end items-baseline p-4">
                {error && (
                  <div>
                    <span
                      style={{
                        color: 'red',
                        fontWeight: 500,
                        fontSize: '12.25px',
                      }}
                    >
                      {t('ERROR_WITH_MESSAGE', { error: t(error) })}{' '}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex justify-end button-group">
                <button
                  className="block button secondary-action"
                  onClick={this.handleHide}
                >
                  {titleize(t('CLOSE'))}
                </button>
                <button
                  className="button button-green flex items-center"
                  disabled={false}
                  onClick={this.sendAll}
                >
                  {saving && (
                    <div className="flex items-center mr-2">
                      <i className="fa fa-spinner fa-spin" />
                    </div>
                  )}
                  <span>{t('SEND_ALL')}</span>
                </button>
              </div>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

BulkUpdateSendDOModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  shipments: PropTypes.array,
  send_do: PropTypes.bool,
  title: PropTypes.string.isRequired,
};

function groupShipmentsByCarrier(shipments) {
  const noCarrier = {
    _id: -1,
  };

  const groupMap = shipments.reduce((group, shipment) => {
    const carrier = shipment.carrier || noCarrier;
    const { _id: carrierId } = carrier;

    const { equipment, ...currentShipment } = shipment;

    currentShipment.equipments = getShipmentEquipment(shipment);
    if (group[carrierId]) {
      const carrierGroup = group[carrierId];
      carrierGroup.shipments.push(currentShipment);
      return {
        ...group,
        [carrierId]: carrierGroup,
      };
    }
    const name = currentShipment.carrier_name || i18next.t('NO_CARRIER');
    return {
      ...group,
      [carrierId]: {
        id: carrierId,
        name,
        shipments: [currentShipment],
      },
    };
  }, {});

  return Object.values(groupMap);
}

export default withTranslation()(BulkUpdateSendDOModal);
